import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "left" | "right" | "none";
}

export const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className,
  delay = 0,
  direction = "up",
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!el || motion?.matches || direction === "none" ||
      typeof IntersectionObserver === "undefined" || !el.animate) return;

    let animation: Animation | undefined;
    const offset = {
      up: "translateY(16px)",
      left: "translateX(-16px)",
      right: "translateX(16px)",
    }[direction];

    // Content stays readable in the server HTML and before JavaScript runs.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.unobserve(el);
          if (!motion?.matches) {
            animation = el.animate(
              [{ transform: offset }, { transform: "translate(0, 0)" }],
              { duration: 450, delay, easing: "ease-out" },
            );
          }
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    const stopMotion = () => {
      if (motion?.matches) animation?.cancel();
    };
    motion?.addEventListener("change", stopMotion);
    return () => {
      observer.disconnect();
      animation?.cancel();
      motion?.removeEventListener("change", stopMotion);
    };
  }, [delay, direction]);

  return (
    <div
      ref={ref}
      className={cn("opacity-100", className)}
    >
      {children}
    </div>
  );
};
